
import { Loader2, Sparkles, Send } from 'lucide-react';
import { Expert } from '../../../types';

interface LogEntry {
    id: number;
    timestamp: string;
    record: string;
    msg: string;
    details?: string;
}

interface Props {
    isGenerating: boolean;
    generationProgress: { current: number; total: number };
    experts: Expert[];
    selectedExpertIds: Set<number>;
    onSelectExpert: (id: number) => void;
    onSelectAllExperts: (select: boolean) => void;
    pubDateFrom: string;
    onPubDateFromChange: (value: string) => void;
    pubDateTo: string;
    onPubDateToChange: (value: string) => void;
    timeFrom: string;
    onTimeFromChange: (value: string) => void;
    timeTo: string;
    onTimeToChange: (value: string) => void;
    promptTemplate: string;
    onPromptTemplateChange: (value: string) => void;
    promptTextareaRef: React.RefObject<HTMLTextAreaElement>;
    placeholders: string[];
    onGenerate: () => void;
    missingRecordsCount: number;
    generatorLogs: LogEntry[];
    setPromptModalContent: (content: string | null) => void;
    rangeError: string | null;
}

const AIGeneratorPanel: React.FC<Props> = ({
    isGenerating,
    generationProgress,
    experts,
    selectedExpertIds,
    onSelectExpert,
    onSelectAllExperts,
    pubDateFrom,
    onPubDateFromChange,
    pubDateTo,
    onPubDateToChange,
    timeFrom,
    onTimeFromChange,
    timeTo,
    onTimeToChange,
    promptTemplate,
    onPromptTemplateChange,
    promptTextareaRef,
    placeholders,
    onGenerate,
    missingRecordsCount,
    generatorLogs,
    setPromptModalContent,
    rangeError
}) => {

    const progressPercent = generationProgress.total > 0 ? (generationProgress.current / generationProgress.total) * 100 : 0;

    const insertPlaceholder = (placeholder: string) => {
        if (promptTextareaRef.current) {
            const start = promptTextareaRef.current.selectionStart;
            const end = promptTextareaRef.current.selectionEnd;
            const text = promptTemplate;
            const newText = text.substring(0, start) + placeholder + text.substring(end);
            onPromptTemplateChange(newText);
            
            setTimeout(() => {
                promptTextareaRef.current?.focus();
                promptTextareaRef.current!.selectionStart = promptTextareaRef.current!.selectionEnd = start + placeholder.length;
            }, 0);
        }
    };

    const selectableExperts = experts.filter(e => e.user_id);

    return (
        <div className="col-span-3">
            {isGenerating && (
                <div className="mb-4 bg-mystic-box p-3 rounded-lg border border-gray-800">
                    <h3 className="text-xs font-bold text-gray-400 mb-2">GENERATION PROGRESS</h3>
                    <div className="w-full bg-gray-700 rounded-full h-2.5">
                        <div className="bg-mystic-accent h-2.5 rounded-full" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>{generationProgress.current} / {generationProgress.total}</span>
                        <span>{Math.round(progressPercent)}%</span>
                    </div>
                </div>
            )}
            <div className="bg-mystic-box border border-gray-800 p-4 rounded-lg">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-mystic-accent" /> AI Generator
                </h2>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="text-xs text-gray-400 font-bold">Authors for Generation</label>
                            <div className="flex gap-2">
                                <button onClick={() => onSelectAllExperts(true)} className="text-xs text-green-400 hover:underline">All</button>
                                <button onClick={() => onSelectAllExperts(false)} className="text-xs text-red-400 hover:underline">None</button>
                            </div>
                        </div>
                        <div className="max-h-32 overflow-y-auto custom-scrollbar border border-gray-700 bg-mystic-inset rounded p-2">
                            {selectableExperts.map(expert => {
                                let fields: string[] = [];
                                try {
                                    const parsedFields = JSON.parse(expert.field || '[]');
                                    if (Array.isArray(parsedFields)) {
                                        fields = parsedFields;
                                    }
                                } catch (_e) { /* ignore parse error */ }
                                const fieldsText = fields.join(', ');

                                return (
                                    <label key={expert.user_id} className="flex items-center gap-2 p-1 hover:bg-white/5 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox bg-gray-800 border-gray-600 text-mystic-accent focus:ring-mystic-accent/50 shrink-0"
                                            checked={selectedExpertIds.has(expert.user_id!)}
                                            onChange={() => onSelectExpert(expert.user_id!)}
                                        />
                                        <div className="truncate">
                                            <span className="text-sm text-gray-300">{expert.name}</span>
                                            {fieldsText && <span className="text-xs text-gray-500 ml-2">{fieldsText}</span>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 font-bold">Publication Date Range</label>
                        <p className="text-[10px] text-gray-500 mt-0.5 mb-1">
                            Losowany dzień publikacji (oraz dodatkowy limit: bez dat po dzisiu w Polsce). Przecięcie z okresem rekordu.
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                type="date"
                                value={pubDateFrom}
                                onChange={e => onPubDateFromChange(e.target.value)}
                                className="w-full bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"
                            />
                            <span className="text-gray-500">-</span>
                            <input
                                type="date"
                                value={pubDateTo}
                                onChange={e => onPubDateToChange(e.target.value)}
                                className="w-full bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 font-bold">Publication Time Range</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input type="time" value={timeFrom} onChange={e => onTimeFromChange(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"/>
                            <span className="text-gray-500">-</span>
                            <input type="time" value={timeTo} onChange={e => onTimeToChange(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"/>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 font-bold">Prompt Template</label>
                        <textarea ref={promptTextareaRef} value={promptTemplate} onChange={e => onPromptTemplateChange(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs mt-1 h-40 resize-y"></textarea>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {placeholders.map(tag => (
                            <button key={tag} onClick={() => insertPlaceholder(tag)} className="text-[10px] font-mono bg-gray-700 text-gray-300 px-2 py-0.5 rounded hover:bg-gray-600">
                                {tag}
                            </button>
                        ))}
                    </div>
                    {rangeError && (
                        <div className="text-xs text-red-400 border border-red-900/50 bg-red-900/15 rounded p-2">{rangeError}</div>
                    )}
                    <button onClick={onGenerate} disabled={isGenerating || missingRecordsCount === 0 || !!rangeError} className="w-full bg-mystic-accent text-black font-bold py-2 rounded flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-mystic-accentHover">
                        {isGenerating ? <Loader2 className="animate-spin w-4 h-4"/> : <Send className="w-4 h-4"/>}
                        {isGenerating ? 'Generating...' : `Generate (${missingRecordsCount}) Records`}
                    </button>
                </div>
                {generatorLogs.length > 0 && (
                    <div className="mt-4 border-t border-gray-700 pt-4">
                        <h4 className="text-xs text-gray-400 font-bold mb-2">Logs</h4>
                        <div className="max-h-40 overflow-y-auto text-xs font-mono text-gray-500 space-y-2 pr-2 custom-scrollbar">
                            {generatorLogs.map(log => (
                                <div key={log.id} className="leading-tight">
                                    <span className="text-gray-600">{log.timestamp}</span>
                                    <span className="text-mystic-accent"> [{log.record}]</span>
                                    <p className="pl-2 text-gray-400 whitespace-pre-wrap">{log.msg}
                                    {log.details && (
                                        <button onClick={() => setPromptModalContent(log.details!)} className="ml-2 text-blue-400 hover:underline text-[10px] font-sans">[View Prompt]</button>
                                    )}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIGeneratorPanel;